import React from "react"
import { useRouter } from "next/router"

import {
  resolveMetricDescription,
  useMetricDirectory,
} from "@/metrics/registry"
import { Tooltip } from "@coval/ui"
import {
  Button,
  Chip,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  useDisclosure,
} from "@heroui/react"
import { Calendar, ExternalLink, User } from "lucide-react"

import { useQueryParams } from "@/hooks/useQueryParams"
import type { MetricRecordData } from "@/types/metrics/contracts"

interface ClickableMetricTitleProps {
  metric: MetricRecordData
  className?: string
  displayText?: string
}

export const ClickableMetricTitle = React.forwardRef<
  HTMLButtonElement,
  ClickableMetricTitleProps
>(({ metric, className = "", displayText }, ref) => {
  const router = useRouter()
  const queryParams = useQueryParams()
  const { isOpen, onOpen, onOpenChange } = useDisclosure()
  const { byManager } = useMetricDirectory()

  const displayName =
    displayText || metric.metric_display_name || metric.metric_name
  const description =
    resolveMetricDescription(
      byManager[metric.metric_manager ?? ""],
      metric.description,
    ) ||
    metric.metric_metadata?.question ||
    "No description available"

  const handleTitleClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    onOpen()
  }

  const getMetricUrl = () => {
    const slug = router.query.slug as string
    if (metric.id) {
      return queryParams.getHref(`/metrics/${metric.id}`, slug)
    }
    return queryParams.getHref("/metrics", slug)
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) {
      return "N/A"
    }
    return new Date(dateString).toLocaleDateString()
  }

  const getMetricTypeDisplay = () => {
    if (metric.metric_manager) {
      if (metric.metric_manager.includes("default_categorical")) {
        return "Categorical"
      }
      if (metric.metric_manager === "manager_llm_binary") {
        return "Binary LLM Judge"
      }
      if (
        metric.metric_manager.includes("numeric") ||
        metric.metric_manager.includes("numerical")
      ) {
        return "Numerical LLM Judge"
      }
    }

    if (metric.metric_metadata?.question) {
      if (
        metric.metric_metadata.additional_information?.key === "categories" &&
        metric.metric_metadata.additional_information?.value
      ) {
        return "Categorical"
      }
      if (
        metric.metric_metadata.min_value !== undefined &&
        metric.metric_metadata.max_value !== undefined
      ) {
        return "Numerical LLM Judge"
      }
      return "Binary LLM Judge"
    }

    return metric.metric_manager || "Custom"
  }

  return (
    <>
      <button
        ref={ref}
        className={`text-left cursor-pointer ${className}`}
        onClick={handleTitleClick}
        type="button"
      >
        <Tooltip content={description} className="max-w-xs p-2 text-sm">
          <span>{displayName}</span>
        </Tooltip>
      </button>

      <Modal
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        size="lg"
        scrollBehavior="inside"
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">
                <h2 className="text-xl font-semibold">{displayName}</h2>
                <p className="text-sm text-coval-text-muted">
                  {metric.metric_name}
                </p>
              </ModalHeader>
              <ModalBody>
                <div className="space-y-4">
                  <div>
                    <h3 className="text-sm font-medium text-coval-text mb-2">
                      Description
                    </h3>
                    <div className="p-3 bg-content2 rounded-md">
                      <p className="text-sm whitespace-pre-wrap wrap-break-word">
                        {description}
                      </p>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-sm font-medium text-coval-text mb-2">
                      Metric Details
                    </h3>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-coval-text-muted">
                          Type:
                        </span>
                        <Chip size="sm" variant="flat" color="primary">
                          {getMetricTypeDisplay()}
                        </Chip>
                      </div>

                      {metric.metric_metadata?.units && (
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-coval-text-muted">
                            Units:
                          </span>
                          <span className="text-sm font-medium">
                            {metric.metric_metadata.units}
                          </span>
                        </div>
                      )}

                      {metric.metric_metadata?.min_value !== undefined &&
                        metric.metric_metadata?.max_value !== undefined && (
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-coval-text-muted">
                              Range:
                            </span>
                            <span className="text-sm font-medium">
                              {metric.metric_metadata.min_value} -{" "}
                              {metric.metric_metadata.max_value}
                            </span>
                          </div>
                        )}

                      {metric.category && (
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-coval-text-muted">
                            Category:
                          </span>
                          <Chip size="sm" variant="flat">
                            {metric.category}
                          </Chip>
                        </div>
                      )}

                      <div className="flex justify-between items-center">
                        <span className="text-sm text-coval-text-muted">
                          Status:
                        </span>
                        <Chip
                          size="sm"
                          variant="flat"
                          color={metric.active ? "success" : "default"}
                        >
                          {metric.active ? "Active" : "Inactive"}
                        </Chip>
                      </div>

                      {metric.locked && (
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-coval-text-muted">
                            Locked:
                          </span>
                          <Chip size="sm" variant="flat" color="warning">
                            Yes
                          </Chip>
                        </div>
                      )}
                    </div>
                  </div>

                  {(metric.created_at ||
                    metric.created_by ||
                    metric.last_updated_at ||
                    metric.last_updated_by) && (
                    <div>
                      <h3 className="text-sm font-medium text-coval-text mb-2">
                        Metadata
                      </h3>
                      <div className="space-y-2">
                        {metric.created_at && (
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-coval-text-muted" />
                            <span className="text-sm text-coval-text-muted">
                              Created:
                            </span>
                            <span className="text-sm">
                              {formatDate(metric.created_at)}
                            </span>
                          </div>
                        )}
                        {metric.created_by && (
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-coval-text-muted" />
                            <span className="text-sm text-coval-text-muted">
                              Created by:
                            </span>
                            <span className="text-sm">{metric.created_by}</span>
                          </div>
                        )}
                        {metric.last_updated_at && (
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-coval-text-muted" />
                            <span className="text-sm text-coval-text-muted">
                              Last updated:
                            </span>
                            <span className="text-sm">
                              {formatDate(metric.last_updated_at)}
                            </span>
                          </div>
                        )}
                        {metric.last_updated_by && (
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-coval-text-muted" />
                            <span className="text-sm text-coval-text-muted">
                              Updated by:
                            </span>
                            <span className="text-sm">
                              {metric.last_updated_by}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {metric.metric_metadata?.additional_information && (
                    <div>
                      <h3 className="text-sm font-medium text-coval-text mb-2">
                        Additional Information
                      </h3>
                      <div className="p-3 bg-content2 rounded-md">
                        <div className="flex justify-between items-start">
                          <span className="text-sm text-coval-text-muted">
                            {metric.metric_metadata.additional_information
                              .key === "categories"
                              ? "Categories"
                              : metric.metric_metadata.additional_information
                                  .key}
                            :
                          </span>
                          <span className="text-sm ml-2">
                            {metric.metric_metadata.additional_information
                              .key === "categories" &&
                            typeof metric.metric_metadata.additional_information
                              .value === "string"
                              ? metric.metric_metadata.additional_information.value
                                  .split(",")
                                  .map((cat: string) => cat.trim())
                                  .join(", ")
                              : metric.metric_metadata.additional_information
                                  .value}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </ModalBody>
              <ModalFooter>
                <Button
                  as={Link}
                  href={getMetricUrl()}
                  color="primary"
                  variant="flat"
                  startContent={<ExternalLink className="w-4 h-4" />}
                  className="mr-auto"
                >
                  View Metric
                </Button>
                <Button color="danger" variant="light" onPress={onClose}>
                  Close
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  )
})

ClickableMetricTitle.displayName = "ClickableMetricTitle"
